import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "../shared";
import { CustomDropdown, DateField, TextInput } from "../atoms";
import {
  useCreateTaskMutation,
  useGetTaskProjectsQuery,
  useGetTaskAssigneesQuery,
  useGetTaskLabelsQuery,
} from "../../store";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import RichTextEditor from "./RichTextEditor";
import FileDropzone from "./FileDropzone";
import { MAX_ASSIGNEES } from "./AssigneeChips";
import useAttachmentUpload, { validateFile } from "./useAttachmentUpload";
import { PRIORITIES, TYPES, formatFileSize, toDayString, userName } from "./taskConstants";

export const TITLE_MAX = 200;
const toOptions = (list) => list.map((x) => ({ id: x.id, label: x.label }));
const TYPE_OPTIONS = toOptions(TYPES);
const PRIORITY_OPTIONS = toOptions(PRIORITIES);

const errText = (e, fallback) => e?.data?.message || e?.message || fallback;

function Label({ children, required }) {
  return (
    <label className="block text-sm font-SemiBold text-gray-700 mb-1.5">
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}

// Yeni İş penceresi. Kayıttan sonra seçilen dosyalar yeni iş id'siyle yüklenir;
// bitince onCreated(task) çağrılır (sayfa ?is=<key> açar).
function NewTaskForm({ open, onClose, defaultProjectId, onCreated }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showError, showWarning } = useAlert();

  const { data: projectsData } = useGetTaskProjectsQuery({ token });
  const projects = useMemo(
    () => (projectsData?.data?.projects || []).filter((p) => p?.can?.createTask && !p.archivedAt),
    [projectsData]
  );

  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("task");
  const [priority, setPriority] = useState("medium");
  const [assignees, setAssignees] = useState([]); // 1.8 — { id, label }[]
  const [dueDate, setDueDate] = useState(null);
  const [labelIds, setLabelIds] = useState([]);
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // { task, files }
  const [progress, setProgress] = useState({});
  const mounted = useRef(true);
  const uploadStarted = useRef(null);

  const [createTask, { isLoading: creating }] = useCreateTaskMutation();
  const { upload } = useAttachmentUpload(created?.task?._id);
  const { data: assigneeData, isFetching: assigneesLoading } = useGetTaskAssigneesQuery({ token, search });
  const { data: labelData } = useGetTaskLabelsQuery({ token });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Varsayılan proje: defaultProjectId (yetkiliyse) yoksa ilk yetkili proje
  useEffect(() => {
    if (!open) return;
    setProjectId((cur) => {
      if (cur && projects.some((p) => p._id === cur)) return cur;
      const def = projects.find((p) => p._id === defaultProjectId) || projects[0];
      return def ? def._id : "";
    });
  }, [open, projects, defaultProjectId]);

  // Kayıttan sonra ekleri sırayla yükle; hata toast, kapanışı engellemez
  useEffect(() => {
    if (!created || uploadStarted.current === created.task._id) return;
    uploadStarted.current = created.task._id;
    (async () => {
      for (let i = 0; i < created.files.length; i += 1) {
        const file = created.files[i];
        try {
          await upload(file, {
            onProgress: (pct) => mounted.current && setProgress((p) => ({ ...p, [i]: pct })),
          });
          if (mounted.current) setProgress((p) => ({ ...p, [i]: 100 }));
        } catch (e) {
          showError(`${file.name}: ${errText(e, "Dosya yüklenemedi.")}`);
        }
      }
      if (mounted.current) onCreated?.(created.task);
    })();
  }, [created, upload, onCreated, showError]);

  const assigneeOptions = useMemo(() => {
    const list = (assigneeData?.data?.users || []).map((u) => ({ id: u._id, label: userName(u) }));
    // Aramada görünmeyen seçililer de listede kalsın
    assignees.forEach((a) => {
      if (!list.some((o) => o.id === a.id)) list.unshift(a);
    });
    return list;
  }, [assigneeData, assignees]);

  const labelOptions = useMemo(
    () => (labelData?.data?.labels || []).map((l) => ({ id: l._id, label: l.name })),
    [labelData]
  );

  const projectOptions = useMemo(
    () => projects.map((p) => ({ id: p._id, label: `${p.key} · ${p.name}` })),
    [projects]
  );

  const trimmed = title.trim();
  const titleValid = trimmed.length >= 1 && trimmed.length <= TITLE_MAX;
  const finishing = Boolean(created);
  const canSave = titleValid && Boolean(projectId) && !creating && !finishing;

  const addFiles = (list) => {
    const ok = [];
    list.forEach((f) => {
      const msg = validateFile(f);
      if (msg) showWarning(`${f.name}: ${msg}`);
      else ok.push(f);
    });
    if (ok.length) setFiles((cur) => [...cur, ...ok]);
  };

  const submit = async () => {
    if (!canSave) return;
    setError("");
    try {
      const res = await createTask({
        token,
        project: projectId,
        title: trimmed,
        description,
        type,
        priority,
        assignees: assignees.map((a) => a.id),
        labels: labelIds,
        dueDate: toDayString(dueDate),
      }).unwrap();
      const task = res?.data?.task;
      if (!task) throw new Error("İş oluşturulamadı.");
      if (files.length) setCreated({ task, files });
      else onCreated?.(task);
    } catch (e) {
      const msg = errText(e, "İş oluşturulamadı.");
      setError(msg);
      showError(msg);
    }
  };

  const close = finishing || creating ? undefined : onClose;

  return (
    <Modal open={open} onClose={close} maxWidth="2xl">
      <Modal.Header title="Yeni İş" onClose={close} />
      <Modal.Body className="max-h-[70vh] overflow-y-auto space-y-4">
        {projects.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="no-project">
            İş açabileceğiniz bir proje yok.
          </p>
        ) : (
          <>
            <div>
              <Label required>Proje</Label>
              <CustomDropdown
                showSearch={false}
                options={projectOptions}
                value={projectOptions.find((o) => o.id === projectId) || null}
                onChange={(o) => o && setProjectId(o.id)}
              />
            </div>

            <TextInput
              label="Başlık"
              required
              value={title}
              maxLength={TITLE_MAX}
              placeholder="Kısa ve açık bir başlık"
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Tür</Label>
                <CustomDropdown
                  showSearch={false}
                  options={TYPE_OPTIONS}
                  value={TYPE_OPTIONS.find((o) => o.id === type)}
                  onChange={(o) => o && setType(o.id)}
                />
              </div>
              <div>
                <Label>Öncelik</Label>
                <CustomDropdown
                  showSearch={false}
                  options={PRIORITY_OPTIONS}
                  value={PRIORITY_OPTIONS.find((o) => o.id === priority)}
                  onChange={(o) => o && setPriority(o.id)}
                />
              </div>
              <div>
                <Label>Atanan</Label>
                <CustomDropdown
                  multi
                  placeholder="Atanmamış"
                  options={assigneeOptions}
                  loading={assigneesLoading}
                  onSearch={setSearch}
                  searchPlaceholder="Kişi ara..."
                  value={assignees}
                  onChange={(sel) => {
                    if (sel.length > MAX_ASSIGNEES) showWarning(`En fazla ${MAX_ASSIGNEES} kişi atanabilir.`);
                    setAssignees(sel.slice(0, MAX_ASSIGNEES));
                  }}
                />
              </div>
              <DateField
                label="Bitiş"
                format="dd.MM.yyyy"
                value={dueDate}
                onChange={(d) => setDueDate(d)}
              />
            </div>

            <div>
              <Label>Etiketler</Label>
              <CustomDropdown
                multi
                placeholder="Etiket seç"
                options={labelOptions}
                value={labelOptions.filter((o) => labelIds.includes(o.id))}
                onChange={(sel) => setLabelIds(sel.map((o) => o.id))}
              />
            </div>

            <div>
              <Label>Açıklama</Label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Ayrıntıları yazın"
              />
              <p className="text-xs text-gray-400 mt-1">Resim eklemek için önce işi kaydedin</p>
            </div>

            <div>
              <Label>Ekler</Label>
              <FileDropzone onFiles={addFiles} disabled={finishing} />
              {files.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="pending-files">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between gap-2 text-sm text-gray-700 rounded-lg bg-gray-50 px-3 py-1.5"
                    >
                      <span className="truncate">{f.name}</span>
                      <span className="flex items-center gap-2 shrink-0 text-xs text-gray-500">
                        {formatFileSize(f.size)}
                        {finishing ? (
                          <span>{progress[i] != null ? `%${progress[i]}` : "Bekliyor"}</span>
                        ) : (
                          <button
                            type="button"
                            aria-label={`${f.name} dosyasını çıkar`}
                            onClick={() => setFiles((cur) => cur.filter((_, idx) => idx !== i))}
                            className="text-gray-400 hover:text-fifth"
                          >
                            Çıkar
                          </button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-2">
        <button
          type="button"
          onClick={close}
          disabled={!close}
          className="px-3 py-1.5 rounded-lg text-sm font-Medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Vazgeç
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSave}
          className="px-4 py-1.5 rounded-lg text-sm font-Medium bg-main text-white hover:opacity-90 disabled:opacity-50"
        >
          {creating ? "Kaydediliyor..." : finishing ? "Ekler yükleniyor..." : "Kaydet"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// Kapalıyken hiçbir şey bağlanmaz; her açılış temiz bir form (kapanınca state sıfırlanır).
function NewTaskModal(props) {
  if (!props.open) return null;
  return <NewTaskForm {...props} />;
}

export default NewTaskModal;
